import { Button, Checkbox, DateText, Input, useToast } from '@/components';
import { toggleInSet } from '@/lib/set';
import {
  ApiClientError,
  GITHUB_USERNAME_RE,
  type UpdateUserBody,
  type UserListItem,
  normalizeGithubUsername,
} from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';
import { RoleMultiSelect } from './RoleMultiSelect';
import { useRoles, useUpdateUser } from './hooks';

export const EditUserForm = (props: {
  user: UserListItem;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const roles = useRoles();
  const update = useUpdateUser(props.user.id);
  const toast = useToast();
  const [roleIds, setRoleIds] = createSignal<Set<string>>(
    new Set(props.user.roles.map((r) => r.id)),
  );
  const [github, setGithub] = createSignal(props.user.githubUsername ?? '');
  const [locked, setLocked] = createSignal(props.user.githubUsernameLocked);
  const [resetWelcome, setResetWelcome] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const toggleRole = (roleId: string) => setRoleIds((cur) => toggleInSet(cur, roleId));

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (roleIds().size === 0) {
      setError('At least one role is required.');
      return;
    }
    const gh = normalizeGithubUsername(github());
    setGithub(gh);
    if (gh !== '' && !GITHUB_USERNAME_RE.test(gh)) {
      setError('Invalid GitHub username.');
      return;
    }
    // Only send what changed — the github field triggers a live GitHub lookup.
    const ghValue = gh === '' ? null : gh;
    const body: UpdateUserBody = {
      roleIds: [...roleIds()],
      ...(ghValue !== props.user.githubUsername ? { githubUsername: ghValue } : {}),
      ...(locked() !== props.user.githubUsernameLocked ? { githubUsernameLocked: locked() } : {}),
      ...(resetWelcome() ? { welcomedAt: null } : {}),
    };
    try {
      await update.mutateAsync(body);
      toast.success('User updated');
      props.onDone();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.message);
      } else {
        setError((err as Error).message ?? 'Update failed');
      }
    }
  };

  return (
    <form onSubmit={onSubmit} class="flex flex-col gap-4">
      <RoleMultiSelect roles={roles.data?.data ?? []} selected={roleIds()} onToggle={toggleRole} />
      <Input
        label="GitHub username"
        hint="Verified against GitHub on save — pasting a profile URL works too"
        placeholder="octocat"
        value={github()}
        onInput={(e) => setGithub(e.currentTarget.value)}
        class="max-w-sm"
      />
      <Checkbox
        checked={locked()}
        onChange={setLocked}
        label="Lock GitHub username (prevent self-edit)"
      />
      <Show when={props.user.welcomedAt}>
        {(welcomedAt) => (
          <Checkbox
            checked={resetWelcome()}
            onChange={setResetWelcome}
            label={
              <span>
                Reset welcome celebration
                <span class="block text-xs text-neutral-500">
                  Welcomed <DateText value={welcomedAt()} /> — plays again on their next visit
                </span>
              </span>
            }
          />
        )}
      </Show>
      <Show when={error()}>
        <p class="text-xs text-red-600">{error()}</p>
      </Show>
      <div class="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={update.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
};
